import { t } from "@/i18n-js/instance";
import { ConnectionNote } from "@/react/components/ProfileModalV3/Body/ConnectBanner/ConnectionNote";
import { Icon } from "@/react/components/shared/Icon";
import { TippyV2 } from "@/react/components/shared/TippyV2";
import { Typography } from "@/react/components/shared/uikit/Typography";
import {
  dateFormats,
  formattedDateTime,
} from "@/react/helpers/dateTimeHelpers/formattedDateTime";
import { useProfileLinkProps } from "@/react/hooks/profile/useProfileLinkProps";
import { ConnectionButtons } from "../../../ActionButtons/ConnectionButtons";
import { MemberCardImage } from "../../../CardView/MemberCardImage";
import { useDetermineConnectionStatus } from "../../../hooks/useDetermineConnectionStatus";
import { ExpandableBio } from "../ExpandableBio";

export const MobileCommunityMember = ({
  communityMember,
  isLastItem,
}: {
  communityMember: any; // Because we have 3 different queries for the member directory, we need to use any here.
  isLastItem: boolean;
}) => {
  const profileLinkProps = useProfileLinkProps({
    public_uid: communityMember.public_uid,
  });

  const memberConnection =
    communityMember.connection_to_current_community_member;

  const { isOtherUserSentConnection, isActive: isConnected } =
    useDetermineConnectionStatus(memberConnection);

  return (
    <>
      <li
        className="bg-primary group first:mt-4 first:md:mt-0"
        data-testid="community_member"
      >
        <div className="flex w-full gap-x-4">
          <a {...profileLinkProps}>
            <div style={{ aspectRatio: "1 / 1" }} className="h-[60px]">
              <MemberCardImage
                name={communityMember.name}
                src={communityMember.avatar_url}
                className="!rounded-full"
              />
            </div>
          </a>

          <section className="flex min-w-0 flex-1 flex-col gap-y-3">
            <div className="flex flex-col gap-y-2">
              <div className="flex flex-col gap-y-0.5">
                <div className="flex items-center justify-between gap-y-1">
                  <Typography.LabelSm weight="semibold">
                    {communityMember.name}
                  </Typography.LabelSm>
                  {isConnected && (
                    <TippyV2
                      content={t("members_directory.connected_on_hover_text", {
                        date_string: formattedDateTime({
                          dateTime: new Date(
                            communityMember.connection_to_current_community_member.created_at,
                          ),
                          format: dateFormats.short_month_and_date_with_year,
                        }),
                      })}
                      placement="bottom"
                    >
                      <div className="bg-secondary text-dark flex !h-5 items-center justify-center gap-1 rounded-3xl py-[2px] pl-2 pr-3 font-medium">
                        <Icon type="16-check" size={16} className="size-4" />
                        <Typography.LabelXs weight="medium">
                          {t("profile_drawer.connect_banner.states.active")}
                        </Typography.LabelXs>
                      </div>
                    </TippyV2>
                  )}
                </div>

                {communityMember.headline && (
                  <Typography.LabelXs truncate>
                    {communityMember.headline}
                  </Typography.LabelXs>
                )}
              </div>

              {communityMember.bio && (
                <ExpandableBio bio={communityMember.bio} lineClamp={2} />
              )}

              {isOtherUserSentConnection && memberConnection?.note && (
                <div className="-mb-2">
                  <ConnectionNote
                    note={memberConnection?.note}
                    lineClamp={1}
                    collapse
                  />
                </div>
              )}

              <ConnectionButtons member={communityMember} />
            </div>
          </section>
        </div>
      </li>
      {!isLastItem && <div className="border-primary h-4 border-b" />}
    </>
  );
};
